import {
  uiBrandingQueryKey,
  useMutationDeleteUiBranding,
} from "@apis:ui_branding/ui_branding";
import { ConfirmationDialog } from "@components:ConfirmationDialog";
import { useInvalidateQueries } from "@hooks:useInvalidateQueries";
import { useToasterStoreActions } from "@stores:global";
import { useTranslation } from "react-i18next";

interface UiBrandingDeleteModalProps {
  isOpen: boolean;
  onClose: () => void;
  brandingId: string;
  organizationId: string;
  organizationName: string;
  botId?: string | null;
  botName?: string | null;
}

export const UiBrandingDeleteModal = ({
  isOpen,
  onClose,
  brandingId,
  organizationId,
  organizationName,
  botId,
  botName,
}: UiBrandingDeleteModalProps) => {
  const { t } = useTranslation();
  const invalidateQueries = useInvalidateQueries();
  const toastActions = useToasterStoreActions();

  const deleteMutation = useMutationDeleteUiBranding({
    onSuccess: async () => {
      toastActions.setToaster({
        variant: "success",
        message: t("preferences.uiBranding.deleted"),
        open: true,
      });
      await invalidateQueries.run([
        { queryKey: uiBrandingQueryKey.all(true).queryKey },
        { queryKey: uiBrandingQueryKey.all(false).queryKey },
        {
          queryKey: uiBrandingQueryKey.byOrganization(organizationId).queryKey,
        },
        ...(botId
          ? [{ queryKey: uiBrandingQueryKey.byBot(botId).queryKey }]
          : []),
      ]);
      onClose();
    },
    onError: () => {
      toastActions.setToaster({
        open: true,
        message: t("preferences.uiBranding.deleteFailed"),
        variant: "error",
      });
    },
  });

  const handleDelete = async () => {
    await deleteMutation.mutateAsync({ id: brandingId, organizationId, botId });
  };

  const targetName = botName
    ? t("preferences.uiBranding.targetBot", { name: botName })
    : t("preferences.uiBranding.targetOrganization", {
        name: organizationName,
      });

  return (
    <ConfirmationDialog
      open={isOpen}
      onClose={onClose}
      title={t("preferences.uiBranding.deleteTitle")}
      description={t("preferences.uiBranding.deleteConfirm", {
        target: targetName,
      })}
      actionControls={{
        secondary: {
          label: t("common.cancel"),
          onClick: onClose,
          sx: { width: "5rem" },
        },
        primary: {
          sx: {
            width: "5rem",
            backgroundColor: (theme) => theme.palette.error.main,
          },
          label: deleteMutation.isPending ? "..." : t("common.delete"),
          onClick: handleDelete,
          disabled: !brandingId || deleteMutation.isPending,
        },
      }}
    />
  );
};
